/**
 * History Type Definitions
 * Unified undo/redo system with JSON Patch-based change tracking
 */

import type { Layer } from './layers';
import type { TemplateVariable } from './template';
import type { Timeline } from './animation';

// ============================================================================
// JSON PATCH (RFC 6902)
// ============================================================================

/**
 * JSON Patch operation types (subset used by Immer)
 */
export interface PatchOperation {
  op: 'add' | 'remove' | 'replace';
  path: (string | number)[];
  value?: unknown;
}

// ============================================================================
// SNAPSHOTS
// ============================================================================

/**
 * Snapshot of all design state that can be undone/redone
 */
export interface DesignSnapshot {
  layers: Layer[];
  variables: TemplateVariable[];
  timeline: Timeline | null;
}

// ============================================================================
// HISTORY ENTRIES
// ============================================================================

/**
 * Single history entry representing a reversible change
 */
export interface HistoryEntry {
  /** Unique identifier */
  id: string;

  /** Human-readable label for UI ("Add Layer", "Delete Keyframe", etc.) */
  label: string;

  /** When the change was made */
  timestamp: number;

  /** Patches to apply for redo (forward) */
  forwardPatches: PatchOperation[];

  /** Patches to apply for undo (reverse) */
  reversePatches: PatchOperation[];
}

// ============================================================================
// TRANSACTIONS
// ============================================================================

/**
 * Transaction for batching multiple operations into a single undo step
 * Used for drag/resize operations that produce many small changes
 */
export interface HistoryTransaction {
  /** Unique identifier */
  id: string;

  /** Label for the combined operation ("Move Layer", "Resize Shape", etc.) */
  label: string;

  /** Snapshot taken at transaction start */
  startSnapshot: DesignSnapshot;
}

// ============================================================================
// HISTORY STATE
// ============================================================================

/**
 * History store state
 */
export interface HistoryState {
  /** Stack of history entries */
  entries: HistoryEntry[];

  /** Current position in history (-1 = at newest) */
  currentIndex: number;

  /** Active transaction for batching changes */
  activeTransaction: HistoryTransaction | null;

  /** Maximum number of history entries to keep */
  maxEntries: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Maximum number of history entries before oldest are pruned */
export const MAX_HISTORY_ENTRIES = 100;
